import { create } from 'zustand';
import { apiService, PanelFeatureDTO } from '@/services/api.service';
import type { PanelModuleKey } from '@/config/panelModules';

/**
 * Catalogue des modules du panel (Supervision, Configuration, Statistiques) et leur activation
 * GLOBALE. Se combine avec le réglage par projet (projectPages.store) : une entrée de navigation
 * n'apparaît que si les DEUX niveaux l'activent (cf useModuleEnabled).
 *
 * Le catalogue porte aussi le libellé, la description, l'espace, le module parent et les projets
 * actifs par défaut de chaque module : le front n'en code aucun en dur.
 *
 * Même philosophie que projectPages.store : non persisté (un réglage admin doit être visible au
 * rechargement suivant), et une clé absente ou un échec de chargement valent ACTIF pour ne jamais
 * vider la navigation sur une erreur réseau.
 */
export type PanelFeatureKey = PanelModuleKey;

interface PanelFeaturesState {
  /** Catalogue complet remonté par l'API (clé, libellé, espace, description, défauts, actif). */
  features: PanelFeatureDTO[];
  /** Vrai après la première tentative de chargement, réussie OU NON (la nav n'attend pas plus). */
  loaded: boolean;
  fetch: () => Promise<void>;
  /** Actif globalement ? Vrai par défaut, y compris avant chargement. */
  isEnabled: (key: PanelFeatureKey | string) => boolean;
  /** Applique localement un réglage qui vient d'être enregistré, sans refaire un aller-retour. */
  setLocal: (features: PanelFeatureDTO[]) => void;
}

export const usePanelFeaturesStore = create<PanelFeaturesState>((set, get) => ({
  features: [],
  loaded: false,

  fetch: async () => {
    const res = await apiService.getPanelFeatures();
    if (res.data) set({ features: res.data.features, loaded: true });
    else set({ loaded: true });
  },

  isEnabled: (key) => {
    const f = get().features.find((x) => x.key === key);
    return f ? f.enabled : true;
  },

  setLocal: (features) => set({ features }),
}));
